import React, { useState } from 'react';
import { SxProps, Theme } from '@mui/material';

import { InlineNotification, InlineNotificationType } from '../components/InlineNotification';

interface ConfirmationAreaProps {
  sx?: SxProps<Theme>;
  noIcon?: boolean;
}

/**
 * A hook to enable managing confirmation messages within a form.  If the provided message generator returns a
 *   confirmation message, calling `isConfirmed` will return false the first time, and true subsequently.  Render a
 *   <ConfirmationArea /> to display the message within an <InlineNotification />.
 *
 * @param type The type of <InlineNotification /> that should be displayed within the <ConfirmationArea />.
 * @param generateMessage A method that should return a confirmation message, if one is needed based on the component's
 *   current state.
 *
 * @example
 * const { ConfirmationArea, isConfirmed } = useConfirmationMessageGenerator(() => {
 *   if (potentialProblemExists()) {
 *     return warningMessage;
 *   }
 * });
 * // ...
 * return (
 *   <form onSubmit={isConfirmed}>
 *     <ConfirmationArea />
 *     // ...
 *   </form>
 * );
 */
export function useConfirmationMessageGenerator(type: InlineNotificationType, generateMessage: () => string | void) {
  const [messageVisible, setMessageVisible] = useState(false);

  const generator = {
    ConfirmationArea({ noIcon, sx }: ConfirmationAreaProps) {
      const message = generateMessage();
      if (!messageVisible || !message) {
        return null;
      }

      return (
        <InlineNotification sx={sx} noIcon={noIcon} type={type}>
          {message}
        </InlineNotification>
      );
    },

    isConfirmed() {
      if (messageVisible) {
        return true;
      }

      if (generateMessage()) {
        setMessageVisible(true);
        return false;
      }

      return true;
    },

    resetConfirmation() {
      setMessageVisible(false);
    },

    resetConfirmationAnd<A extends any[], R>(nextOperation: (...args: A) => R) {
      return (...args: A) => {
        generator.resetConfirmation();

        return nextOperation(...args);
      };
    },
  };

  return generator;
}
